import React, { useState, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import axios from "axios";
import FalconCloseButton from "components/common/falconCloseButton";
import { useTranslation } from "react-i18next";
import TemplateViewerAdmin from "components/proposals/proposalTemplates/templateViewerAdmin";
import { fetchLogoSrc, fetchRelations } from "../../../assets/js/common";

const ViewProposals = ({ proposalId, show, handleCloseProposal, refreshProposalData, preloadedData }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const [noProposalMessage, setNoProposalMessage] = useState("");
  const customerId = localStorage.getItem("customerId");
  const [logoSrc, setLogoSrc] = useState(null);

  useEffect(() => {
    if (!proposalId) return;

    if (formData?.proposal?.ProposalId && formData.proposal.ProposalId !== proposalId) {
      setFormData(null);
      setNoProposalMessage("");
    }

    if (formData?.proposal?.ProposalId === proposalId) return;

    const hasPreloadedTasks =
      Array.isArray(preloadedData?.tasks) && preloadedData.tasks.length > 0;

    if (!show && !hasPreloadedTasks) return;

    const loadData = async () => {
      setIsLoading(true);

      try {
        let proposalDetails;
        let proposal;
        let selectedSubtasksArray;

        if (preloadedData?.proposalDetails && preloadedData?.proposal && hasPreloadedTasks) {
          proposalDetails = { ...preloadedData.proposalDetails };
          proposal = preloadedData.proposal;

          const currentVersionId = proposal?.CurrentVersionId;
          if (currentVersionId && !proposalDetails.ProposalVersionId) {
            proposalDetails.ProposalVersionId = currentVersionId;
          }

          selectedSubtasksArray = [];
          preloadedData.tasks.forEach((task) => {
            const selectedIds = preloadedData.selectedSubtasksMap?.[task.ProposalTaskId];
            if (!selectedIds) return;
            selectedIds.forEach((subtaskId) => {
              selectedSubtasksArray.push({
                proposalTaskId: task.ProposalTaskId,
                taskId: task.TaskId,
                subtaskId,
                selectedDays: task.SelectedDays ? JSON.parse(task.SelectedDays) : [],
                monthlyDay: task.MonthlyDay || null,
              });
            });
          });
        } else {
          const proposalDetailsRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/getProposalDetailsByProposalId/${proposalId}`,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          );
          proposalDetails = proposalDetailsRes.data;

          if (!proposalDetails) {
            setNoProposalMessage(t("proposalNotCreated"));
            setFormData(null);
            setIsLoading(false);
            return;
          }

          const proposalRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/proposalRoute/getProposalById/${proposalDetails.ProposalId}`,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          );
          proposal = proposalRes.data;

          const currentVersionId = proposal?.CurrentVersionId;
          if (currentVersionId && !proposalDetails.ProposalVersionId) {
            proposalDetails.ProposalVersionId = currentVersionId;
          }

          const tasksRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalDetails.ProposalId}/tasks`,
            {
              params: currentVersionId ? { proposalVersionId: currentVersionId } : {},
              headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
            }
          );
          const proposalTasks = tasksRes.data || [];

          selectedSubtasksArray = [];
          for (const task of proposalTasks) {
            try {
              const selectedSubtasksRes = await axios.get(
                `${process.env.REACT_APP_API_URL}/proposalRoute/proposalTasks/${task.ProposalTaskId}/subtasks`, {
                headers: {
                  Authorization: process.env.REACT_APP_BasicAuthSecret
                }
              }
              );
              const selectedSubtasks = (selectedSubtasksRes.data || []).filter((sub) => sub.selected !== false);

              selectedSubtasks.forEach((sub) => {
                selectedSubtasksArray.push({
                  proposalTaskId: task.ProposalTaskId,
                  taskId: task.TaskId,
                  subtaskId: sub.SubtaskId,
                  selectedDays: task.SelectedDays
                    ? JSON.parse(task.SelectedDays)
                    : [],
                  monthlyDay: task.MonthlyDay || null,
                });
              });
            } catch (err) {
              console.error("Error fetching subtasks:", err);
            }
          }
        }

        const [userRes, relationData, locationRes, settingsRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${proposalDetails.CommunicationUserId}`, {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
          ),
          fetchRelations(proposalDetails.RelationId),
          axios.get(
            `${process.env.REACT_APP_API_URL}/locationRoute/getById/${proposalDetails.LocationId}`, {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
          ),
          axios.get(
            `${process.env.REACT_APP_API_URL}/settingsRoute/getSettingsBranding/${proposal.CustomerId}`,
            {
              headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret,
              },
            }
          ),
        ]);

        const userData = userRes.data;
        if (!proposalDetails?.CompanySignature && userData?.CompanySignature) {
          proposalDetails.CompanySignature = userData.CompanySignature;
        }

        const locationData = locationRes.data;
        const settings = settingsRes.data;

        setFormData({
          proposalDetailsData: proposalDetails,
          proposal,
          userData,
          relationData,
          locationData,
          settings,
          selectedSubtasks: selectedSubtasksArray,
          preloadedTasks: hasPreloadedTasks ? preloadedData.tasks : undefined,
          preloadedAllSubtasksByTaskId: hasPreloadedTasks ? preloadedData.allSubtasksByTaskId : undefined,
          preloadedTaskNameById: hasPreloadedTasks ? preloadedData.taskNameById : undefined,
        });
      } catch (error) {
        console.error("Error loading proposal data:", error);
        setFormData(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [proposalId, show, preloadedData]);

  useEffect(() => {
    const getLogo = async () => {
      const src = await fetchLogoSrc(customerId);
      setLogoSrc(src);
    };

    getLogo();
  }, []);
  return (
    <>
      {noProposalMessage ? (
        <div className="position-relative text-center text-danger">
          <FalconCloseButton
            size="sm"
            style={{ position: "absolute", right: 10, top: 10 }}
            onClick={handleCloseProposal}
          />
          <p className="mt-5 mb-5">{noProposalMessage}</p>
        </div>
      ) : !isLoading && formData ? (
        <Modal
          size="lg"
          show={show}
          onHide={handleCloseProposal}
          backdrop="static"
        >
          <Modal.Header>
            <Modal.Title>
              {formData?.proposalDetailsData?.TemplateName}
            </Modal.Title>
            <FalconCloseButton onClick={handleCloseProposal} />
          </Modal.Header>
          <Modal.Body>
            <TemplateViewerAdmin
              formData={formData}
              workType={formData?.proposalDetailsData?.WorkType}
              logoSrc={logoSrc}
              handleCloseProposal={handleCloseProposal}
              refreshProposalData={refreshProposalData}
            />
          </Modal.Body>
        </Modal>
      ) : show ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : null}
    </>
  );
};

export default ViewProposals;
